import React, { useEffect, useState, useCallback, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  MarkerType,
  useNodesState,
  useEdgesState,
  Panel
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { Maximize2, Minimize2, X, Info, Tag, Database, FileText, Code, ZoomIn, ZoomOut, Maximize, Shield, ArrowRight } from 'lucide-react';

const nodeTypeColors = {
  intent:       { bg: '#e9d5ff', border: '#9333ea', text: '#6b21a8' },
  tool:         { bg: '#fef08a', border: '#cac704', text: '#85830e' },
  tool_executor:{ bg: '#b9fec2', border: '#0bca04', text: '#16850e' },
  error:        { bg: '#fed0aa', border: '#ea0c0c', text: '#9a1212' },
  summary:      { bg: '#bfdbfe', border: '#2563eb', text: '#1e40af' },
  hitl:         { bg: '#fbcfe8', border: '#db2777', text: '#9d174d' },
  guardrail:    { bg: '#feaaaa', border: '#ea0c0c', text: '#9a1212' },
  process:      { bg: '#e2e8f0', border: '#475569', text: '#1e293b' },
};

// v2 colors keyed by catalog type strings
const nodeTypeColorsV2 = {
  guardrails:              { bg: '#fecaca', border: '#dc2626', text: '#991b1b' },
  planner:                 { bg: '#e9d5ff', border: '#9333ea', text: '#6b21a8' },
  active_agent:            { bg: '#fef9c3', border: '#ca8a04', text: '#854d0e' },
  execution_subgraph_node: { bg: '#bbf7d0', border: '#16a34a', text: '#14532d' },
  executor:                { bg: '#bbf7d0', border: '#16a34a', text: '#14532d' },
  step_executor:           { bg: '#bbf7d0', border: '#16a34a', text: '#14532d' },
  argument_builder:        { bg: '#bbf7d0', border: '#16a34a', text: '#14532d' },
  agent_invoker:           { bg: '#bbf7d0', border: '#16a34a', text: '#14532d' },
  hitl:                    { bg: '#fbcfe8', border: '#db2777', text: '#9d174d' },
  error_handler:           { bg: '#fed7aa', border: '#ea580c', text: '#9a3412' },
  responder:               { bg: '#bfdbfe', border: '#2563eb', text: '#1e40af' },
  lifecycle:               { bg: '#e2e8f0', border: '#475569', text: '#1e293b' },
};

const edgeColors = {
  default:      '#64748b',
  conditional:  '#8b5cf6',
  error:        '#ef4444',
  guardrail:    '#9a1212',
  success:      '#10b981',
  tool_executor:'#16850e',
  end:          '#3b82f6',
  tool:         '#f59e0b',
};

const getNodeType = (id, name, data) => {
  let nameStr = '';
  try {
    nameStr = name ? (typeof name === 'object' ? JSON.stringify(name) : String(name)) : '';
  } catch (e) {
    nameStr = '';
  }
  if (id?.includes('error') || nameStr.includes('Error')) return 'error';
  if (id?.includes('intent') || data?.type === 'intent_node') return 'intent';
  if (id?.includes('summary') || nameStr.includes('Summary')) return 'summary';
  if (id?.includes('HITL') || nameStr.includes('Human')) return 'hitl';
  if (id?.includes('guardrail') || nameStr.includes('Guardrail')) return 'guardrail';
  if (id?.includes('executor') || nameStr.includes('Executor')) return 'tool_executor';
  if (id?.includes('tool') || data?.type === 'tool') return 'tool';
  return 'process';
};

const getNodeTypeV2 = (id, data) => {
  const type = data?.type;
  if (type && nodeTypeColorsV2[type]) return type;
  if (id?.startsWith('agent::')) return 'active_agent';
  return 'lifecycle';
};

const getEdgeType = (edge) => {
  const edgeData = typeof edge.data === 'object' ? JSON.stringify(edge.data) : String(edge.data || '');
  if (edgeData === 'error') return 'error';
  if (edgeData === '__end__') return 'end';
  if (edgeData === 'tool') return 'tool';
  if (edge.conditional) return 'conditional';
  return 'default';
};

const getEdgeColor = (edge) => edgeColors[getEdgeType(edge)] || edgeColors.default;

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });
  nodes.forEach((node) => dagreGraph.setNode(node.id, { width: 180, height: 70 }));
  edges.forEach((edge) => dagreGraph.setEdge(edge.source, edge.target));
  dagre.layout(dagreGraph);
  return {
    nodes: nodes.map((node) => {
      const pos = dagreGraph.node(node.id);
      return { ...node, position: { x: pos.x - 90, y: pos.y - 35 } };
    }),
    edges,
  };
};

const safeRenderValue = (value) => {
  if (value === null || value === undefined) return '';
  try {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  } catch (e) {
    return '[Complex Object]';
  }
};

/* ─── Shared modal shell ──────────────────────────────────────── */
const ModalShell = ({ isVisible, onClose, isFullScreen, borderColor, children }) => (
  <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: isFullScreen ? 60 : 50 }}>
    <div
      className={`absolute inset-0 backdrop-blur-sm bg-slate-900/30 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      onClick={onClose}
    />
    <div
      className={`bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 transform ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
      style={{
        width: 'min(400px, 90vw)',
        maxHeight: '75vh',
        boxShadow: `0 10px 25px -5px rgba(0,0,0,0.15), 0 0 0 1px ${borderColor}40`,
      }}
    >
      {children}
    </div>
  </div>
);

/* ─── Node details modal ──────────────────────────────────────── */
const NodeDetailsModal = ({ node, onClose, isFullScreen, useNovaV2 = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  if (!node) return null;

  const rawData = node.originalData || {};
  const nodeType = useNovaV2
    ? getNodeTypeV2(rawData.id, rawData.data)
    : getNodeType(rawData.id, safeRenderValue(rawData.name), rawData.data);
  const colorMap = useNovaV2 ? nodeTypeColorsV2 : nodeTypeColors;
  const colors = colorMap[nodeType] || (useNovaV2 ? nodeTypeColorsV2.lifecycle : nodeTypeColors.process);

  const extraMeta = rawData.metadata
    ? Object.fromEntries(Object.entries(rawData.metadata).filter(([k]) => !['description', 'tags'].includes(k)))
    : {};

  return (
    <ModalShell isVisible={isVisible} onClose={onClose} isFullScreen={isFullScreen} borderColor={colors.border}>
      {/* Header */}
      <div
        className="px-4 py-3 flex justify-between items-center border-b shrink-0"
        style={{ background: colors.bg, borderColor: colors.border + '80' }}
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: colors.border + '25' }}>
            {nodeType === 'tool' && <Database className="w-3.5 h-3.5" style={{ color: colors.text }} />}
            {nodeType === 'tool_executor' && <Database className="w-3.5 h-3.5" style={{ color: colors.text }} />}
            {nodeType === 'guardrail' && <Shield className="w-3.5 h-3.5" style={{ color: colors.text }} />}
            {nodeType === 'intent' && <FileText className="w-3.5 h-3.5" style={{ color: colors.text }} />}
            {nodeType === 'process' && <Code className="w-3.5 h-3.5" style={{ color: colors.text }} />}
            {!['tool', 'tool_executor', 'intent', 'process', 'guardrail'].includes(nodeType) && (
              <Info className="w-3.5 h-3.5" style={{ color: colors.text }} />
            )}
          </div>
          <span className="text-sm font-bold" style={{ color: colors.text }}>
            {rawData.name || 'Node Details'}
          </span>
        </div>
        <button onClick={onClose} className="p-1 rounded-full hover:bg-black/10 transition-colors" style={{ color: colors.text }}>
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4 overflow-y-auto flex-grow space-y-3">
        {/* ID */}
        <Section icon={<Code className="w-3.5 h-3.5 text-gray-500" />} label="Node ID">
          <code className="block bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs font-mono text-gray-700 break-all">
            {rawData.id}
          </code>
        </Section>

        {/* Type badge */}
        <Section icon={<Info className="w-3.5 h-3.5 text-gray-500" />} label="Type">
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium"
            style={{ backgroundColor: colors.bg + '60', borderColor: colors.border + '60', color: colors.text }}
          >
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: colors.border }} />
            {rawData.data?.type || nodeType}
          </div>
        </Section>

        {rawData.metadata?.description && (
          <Section icon={<FileText className="w-3.5 h-3.5 text-gray-500" />} label="Description">
            <p className="bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs text-gray-700 whitespace-pre-wrap">
              {rawData.metadata.description}
            </p>
          </Section>
        )}

        {rawData.metadata?.tags?.length > 0 && (
          <Section icon={<Tag className="w-3.5 h-3.5 text-gray-500" />} label="Tags">
            <div className="flex flex-wrap gap-1.5">
              {rawData.metadata.tags.map((tag, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{tag}</span>
              ))}
            </div>
          </Section>
        )}

        {rawData.data && Object.keys(rawData.data).length > 0 && (
          <Section icon={<Database className="w-3.5 h-3.5 text-gray-500" />} label="Data">
            <pre className="bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs text-gray-700 overflow-x-auto">
              {JSON.stringify(rawData.data, null, 2)}
            </pre>
          </Section>
        )}

        {Object.keys(extraMeta).length > 0 && (
          <Section icon={<Database className="w-3.5 h-3.5 text-gray-500" />} label="Metadata">
            <pre className="bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs text-gray-700 overflow-x-auto">
              {JSON.stringify(extraMeta, null, 2)}
            </pre>
          </Section>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end shrink-0">
        <button
          onClick={onClose}
          className="px-3 py-1.5 bg-white hover:bg-gray-100 border border-gray-300 rounded-md text-xs text-gray-700 transition-colors font-medium"
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
};

/* ─── Edge details modal ──────────────────────────────────────── */
const EdgeDetailsModal = ({ edge, nodeMap, onClose, isFullScreen }) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  if (!edge) return null;

  const rawEdge = edge.originalData || {};
  const edgeType = getEdgeType(rawEdge);
  const color = edgeColors[edgeType] || edgeColors.default;
  const label = typeof rawEdge.data === 'object'
    ? JSON.stringify(rawEdge.data, null, 2)
    : String(rawEdge.data || '—');

  const sourceName = nodeMap[edge.source]?.name || edge.source;
  const targetName = nodeMap[edge.target]?.name || edge.target;

  return (
    <ModalShell isVisible={isVisible} onClose={onClose} isFullScreen={isFullScreen} borderColor={color}>
      {/* Header */}
      <div
        className="px-4 py-3 flex justify-between items-center border-b shrink-0"
        style={{ background: color + '18', borderColor: color + '60' }}
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: color + '25' }}>
            <ArrowRight className="w-3.5 h-3.5" style={{ color }} />
          </div>
          <span className="text-sm font-bold text-gray-800">Edge Details</span>
        </div>
        <button onClick={onClose} className="p-1 rounded-full hover:bg-black/10 transition-colors text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="p-4 overflow-y-auto flex-grow space-y-3">
        {/* Flow */}
        <Section icon={<ArrowRight className="w-3.5 h-3.5 text-gray-500" />} label="Connection">
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-2 text-xs">
            <code className="text-gray-700 font-mono truncate max-w-[120px]" title={sourceName}>{sourceName}</code>
            <ArrowRight className="w-3 h-3 shrink-0" style={{ color }} />
            <code className="text-gray-700 font-mono truncate max-w-[120px]" title={targetName}>{targetName}</code>
          </div>
        </Section>

        {/* Type */}
        <Section icon={<Info className="w-3.5 h-3.5 text-gray-500" />} label="Type">
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium"
            style={{ backgroundColor: color + '18', borderColor: color + '60', color }}
          >
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            {edgeType.charAt(0).toUpperCase() + edgeType.slice(1)}
            {rawEdge.conditional && ' (conditional)'}
          </div>
        </Section>

        {/* Label / condition */}
        {rawEdge.data !== undefined && rawEdge.data !== null && rawEdge.data !== '' && (
          <Section icon={<Tag className="w-3.5 h-3.5 text-gray-500" />} label="Condition / Label">
            <pre className="bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs font-mono text-gray-700 overflow-x-auto whitespace-pre-wrap break-all">
              {label}
            </pre>
          </Section>
        )}

        {/* IDs */}
        <Section icon={<Code className="w-3.5 h-3.5 text-gray-500" />} label="Source ID">
          <code className="block bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs font-mono text-gray-600 break-all">
            {edge.source}
          </code>
        </Section>
        <Section icon={<Code className="w-3.5 h-3.5 text-gray-500" />} label="Target ID">
          <code className="block bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs font-mono text-gray-600 break-all">
            {edge.target}
          </code>
        </Section>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end shrink-0">
        <button
          onClick={onClose}
          className="px-3 py-1.5 bg-white hover:bg-gray-100 border border-gray-300 rounded-md text-xs text-gray-700 transition-colors font-medium"
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
};

/* ─── Small reusable label+content section ────────────────────── */
const Section = ({ icon, label, children }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-xs font-semibold text-gray-600">{label}</span>
    </div>
    {children}
  </div>
);

/* ─── WorkflowVisualizer ──────────────────────────────────────── */
const WorkflowVisualizer = ({ graphData, useNovaV2 = false }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [nodeMap, setNodeMap] = useState({});
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const nodeTypes = useMemo(() => ({}), []);
  const edgeTypes = useMemo(() => ({}), []);

  const zoomIn = () => reactFlowInstance?.zoomIn();
  const zoomOut = () => reactFlowInstance?.zoomOut();
  const fitView = () => reactFlowInstance?.fitView({ padding: 0.2 });

  const onNodeClick = useCallback((event, node) => {
    setSelectedEdge(null);
    setSelectedNode(node);
  }, []);

  const onEdgeClick = useCallback((event, edge) => {
    setSelectedNode(null);
    setSelectedEdge(edge);
  }, []);

  const closeModals = () => {
    setSelectedNode(null);
    setSelectedEdge(null);
  };

  const reactFlowProps = {
    nodes,
    edges,
    nodeTypes,
    edgeTypes,
    onNodesChange,
    onEdgesChange,
    onNodeClick,
    onEdgeClick,
    fitView: true,
    fitViewOptions: { padding: 0.2 },
    proOptions: { hideAttribution: true },
    onInit: setReactFlowInstance,
  };

  useEffect(() => {
    if (!graphData) return;

    const map = {};
    const flowNodes = Object.entries(graphData.nodes).map(([id, nodeData]) => {
      map[nodeData.id] = nodeData;
      const nodeType = useNovaV2
        ? getNodeTypeV2(nodeData.id, nodeData.data)
        : getNodeType(nodeData.id, nodeData.name, nodeData.data);
      const colorMap = useNovaV2 ? nodeTypeColorsV2 : nodeTypeColors;
      const colors = colorMap[nodeType] || (useNovaV2 ? nodeTypeColorsV2.lifecycle : nodeTypeColors.process);

      return {
        id: nodeData.id,
        data: {
          label: (
            <div className="font-medium">
              <div className="text-sm font-bold">{safeRenderValue(nodeData.name)}</div>
              <div className="text-xs font-semibold opacity-90 mt-1">
                {nodeType.charAt(0).toUpperCase() + nodeType.slice(1)}
              </div>
              {nodeData.data?.type && (
                <div className="text-xs opacity-80 mt-0.5">{safeRenderValue(nodeData.data.type)}</div>
              )}
            </div>
          ),
        },
        position: { x: 0, y: 0 },
        style: {
          background: colors.bg,
          borderColor: colors.border,
          borderWidth: 2,
          color: colors.text,
          borderRadius: 8,
          padding: 10,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        },
        className: 'hover:shadow-lg',
        originalData: nodeData,
      };
    });
    setNodeMap(map);

    const flowEdges = graphData.edges.map((edge, index) => {
      const edgeColor = getEdgeColor(edge);
      const edgeType = getEdgeType(edge);
      const edgeLabel =
        typeof edge.data === 'object'
          ? JSON.stringify(edge.data).substring(0, 20) + (JSON.stringify(edge.data).length > 20 ? '…' : '')
          : String(edge.data || '');

      return {
        id: `e${index}`,
        source: edge.source,
        target: edge.target,
        label: edgeLabel,
        animated: edge.conditional,
        labelStyle: { fontSize: '10px', fill: edgeColor, fontWeight: 500 },
        markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor },
        style: { stroke: edgeColor, strokeWidth: edgeType === 'error' ? 2.5 : 2, cursor: 'pointer' },
        originalData: edge,
      };
    });

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(flowNodes, flowEdges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [graphData, setNodes, setEdges]);

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    setTimeout(() => reactFlowInstance?.fitView({ padding: 0.2 }), 50);
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (selectedNode || selectedEdge) closeModals();
        else if (isFullScreen) setIsFullScreen(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isFullScreen, selectedNode, selectedEdge]);

  if (!graphData) {
    return (
      <div className="flex justify-center items-center h-full bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-gray-500 text-center p-6">
          <p className="mb-2">No workflow data available yet.</p>
          <p className="text-sm">Send a message in the chat to see the agent's workflow.</p>
        </div>
      </div>
    );
  }

  const legendEntries = useNovaV2
    ? [
        ['guardrails',  nodeTypeColorsV2.guardrails,  'Guardrails'],
        ['planner',     nodeTypeColorsV2.planner,     'Planner'],
        ['active_agent',nodeTypeColorsV2.active_agent,'Agents'],
        ['execution_subgraph_node', nodeTypeColorsV2.execution_subgraph_node, 'Subgraph'],
        ['hitl',        nodeTypeColorsV2.hitl,        'HITL'],
        ['error_handler',nodeTypeColorsV2.error_handler,'Error'],
        ['responder',   nodeTypeColorsV2.responder,   'Final Response'],
      ]
    : Object.entries(nodeTypeColors)
        .filter(([t]) => t !== 'process')
        .map(([type, c]) => [type, c, type.charAt(0).toUpperCase() + type.slice(1)]);

  const legend = (
    <div className="bg-white p-2.5 rounded-lg shadow-md border border-gray-200 text-xs">
      <div className="font-semibold mb-1.5 text-gray-700">Node Types</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {legendEntries.map(([type, c, label]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm border" style={{ backgroundColor: c.bg, borderColor: c.border }} />
            <span className="text-gray-600">{label}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 text-gray-400 text-[10px] flex items-center gap-1">
        <Info className="w-3 h-3" />
        Click nodes or edges for details
      </div>
    </div>
  );

  const controls = (
    <Panel position="top-left" className="bg-white rounded-md shadow-md border border-gray-200 p-1 flex space-x-1 m-1">
      <button onClick={zoomIn} className="p-1.5 hover:bg-gray-100 rounded-md transition-colors" title="Zoom In">
        <ZoomIn className="w-4 h-4 text-gray-700" />
      </button>
      <button onClick={zoomOut} className="p-1.5 hover:bg-gray-100 rounded-md transition-colors" title="Zoom Out">
        <ZoomOut className="w-4 h-4 text-gray-700" />
      </button>
      <button onClick={fitView} className="p-1.5 hover:bg-gray-100 rounded-md transition-colors" title="Fit View">
        <Maximize className="w-4 h-4 text-gray-700" />
      </button>
    </Panel>
  );

  return (
    <>
      <NodeDetailsModal node={selectedNode} onClose={closeModals} isFullScreen={isFullScreen} useNovaV2={useNovaV2} />
      <EdgeDetailsModal edge={selectedEdge} nodeMap={nodeMap} onClose={closeModals} isFullScreen={isFullScreen} />

      {isFullScreen ? (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="absolute top-4 right-4 z-10">
            <button onClick={toggleFullScreen} className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors" title="Exit Full Screen">
              <Minimize2 className="w-6 h-6 text-gray-700" />
            </button>
          </div>
          <ReactFlow {...reactFlowProps}>
            <Controls className="bg-white shadow-md rounded-md border border-gray-200" />
            <MiniMap nodeStrokeWidth={3} zoomable pannable className="bg-white shadow-md rounded-md border border-gray-200"
              nodeColor={(node) => {
                try {
                  const nodeId = node.id || '';
                  let nodeLabel = '';
                  if (node.data?.label?.props?.children) {
                    const children = node.data.label.props.children;
                    if (Array.isArray(children) && children[0]?.props?.children) {
                      nodeLabel = safeRenderValue(children[0].props.children);
                    }
                  }
                  const colorMap = useNovaV2 ? nodeTypeColorsV2 : nodeTypeColors;
                  const fallback = useNovaV2 ? nodeTypeColorsV2.lifecycle.border : nodeTypeColors.process.border;
                  const nt = useNovaV2 ? getNodeTypeV2(nodeId, null) : getNodeType(nodeId, nodeLabel, null);
                  return colorMap[nt]?.border || fallback;
                } catch (e) {
                  return useNovaV2 ? nodeTypeColorsV2.lifecycle.border : nodeTypeColors.process.border;
                }
              }}
            />
            {controls}
            <Background variant="dots" gap={12} size={1} color="#f1f5f9" />
          </ReactFlow>
          <div className="absolute bottom-4 left-4">{legend}</div>
        </div>
      ) : (
        <div className="w-full h-[500px] border border-gray-200 rounded-lg shadow-sm bg-white relative">
          <div className="absolute top-2 right-2 z-10">
            <button onClick={toggleFullScreen} className="bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors" title="Full Screen">
              <Maximize2 className="w-5 h-5 text-gray-700" />
            </button>
          </div>
          <ReactFlow {...reactFlowProps}>
            <Controls className="bg-white shadow-md rounded-md border border-gray-200" />
            <MiniMap nodeStrokeWidth={3} zoomable pannable className="bg-white shadow-md rounded-md border border-gray-200"
              nodeColor={(node) => {
                try {
                  const colorMap = useNovaV2 ? nodeTypeColorsV2 : nodeTypeColors;
                  const fallback = useNovaV2 ? nodeTypeColorsV2.lifecycle.border : nodeTypeColors.process.border;
                  const nt = useNovaV2 ? getNodeTypeV2(node.id, null) : getNodeType(node.id, node.data?.label?.props?.children[0]?.props?.children, null);
                  return colorMap[nt]?.border || fallback;
                } catch (e) {
                  return useNovaV2 ? nodeTypeColorsV2.lifecycle.border : nodeTypeColors.process.border;
                }
              }}
            />
            {controls}
            <Background variant="dots" gap={12} size={1} color="#f1f5f9" />
          </ReactFlow>
          <div className="absolute bottom-2 left-2">{legend}</div>
        </div>
      )}
    </>
  );
};

export default WorkflowVisualizer;
